import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { surveyApi } from 'api/survey';
import { usePanel } from 'contexts/panelContext';
import { FinalSurveyPresenter } from 'components/_common/FinalSurveyPresenter';
import { Spinner } from 'components/_new/Spinner';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Button } from 'components/_new/Button';
import iconChecklist from 'assets/icon-checklist.png';
import { useAuth } from 'contexts/authContext';
import { useProgramTranslation } from 'hooks/useProgramTranslation';

type FinalSurveyPanelProps = DialogBaseExtendProps & { withIntro?: boolean };

export const FinalSurveyPanel = ({ withIntro = false, ...dialogProps }: FinalSurveyPanelProps) => {
	const { tProgram } = useProgramTranslation();
	const { currentUser } = useAuth();
	const { currentProgramMembership } = usePanel();

	const { data, isLoading, isSuccess } = useQuery(
		[
			'FINAL_SURVEY',
			{ programMembershipId: Number(currentProgramMembership?.id), language: currentUser?.language },
		],
		() => surveyApi.getSurveyForProgramMembership(Number(currentProgramMembership?.id)),
		{
			enabled: dialogProps.visible && Boolean(currentProgramMembership),
		}
	);

	const queryClient = useQueryClient();
	const { mutateAsync, isLoading: updating } = useMutation(
		({ step, answers }: { step: number; answers: Array<any> }) =>
			surveyApi.updateSurveyForProgramMembership(Number(currentProgramMembership?.id), { step, answers }),
		{
			onSuccess: () => {
				queryClient.refetchQueries([
					'FINAL_SURVEY',
					{ programMembershipId: Number(currentProgramMembership?.id) },
				]);
			},
		}
	);

	const dialogContentRef = useRef<HTMLDivElement>(null);
	const handleSave = (step: number, answers: Array<any>) => {
		dialogContentRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
		return mutateAsync({ step, answers });
	};

	const handleEnd = () => {
		dialogProps.onHide?.();
	};

	const [afterIntro, setAfterIntro] = useState(false);
	const handleEndIntro = useCallback(() => setAfterIntro(true), []);

	return (
		<Dialog
			contentRef={dialogContentRef}
			title={tProgram('userPanel.finalSurvey.title')}
			size="lg"
			{...dialogProps}
		>
			{withIntro && !afterIntro && (
				<div className="flex flex-column gap-2">
					<div className="grid">
						<div className="col-12 md:col-9">
							<p>{tProgram('userPanel.finalSurvey.introText.first')}</p>
							<p>{tProgram('userPanel.finalSurvey.introText.second')}</p>
							<p>{tProgram('userPanel.finalSurvey.introText.third')}</p>
							<p>{tProgram('userPanel.finalSurvey.introText.fourth')}</p>
						</div>
						<div className="hidden md:block md:col-3">
							<img src={iconChecklist} className="w-full" alt="" />
						</div>
					</div>
					<div className="grid">
						<div className="col-12 md:col-7" />
						<div className="col-12 md:col-5">
							<Button
								block
								label={tProgram('userPanel.finalSurvey.startSurvey')}
								onClick={handleEndIntro}
							/>
						</div>
					</div>
				</div>
			)}
			{(!withIntro || afterIntro) && (
				<>
					{isLoading && <Spinner center />}
					{isSuccess && (
						<FinalSurveyPresenter
							panelMode
							survey={data}
							updating={updating}
							onSave={handleSave}
							onEnd={handleEnd}
						/>
					)}
				</>
			)}
		</Dialog>
	);
};
